import _ from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { shouldSelectBundleOption, parseLocationTargets, getLocationIntent, convertLocationStateToLocationSchemaCampaign } from '@bingads-webui-campaign/hotel-common-targets';
import { locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import {
  targetOptions,
} from '@bingads-webui-campaign-react/search-campaign-wizard-controls';
import { getTargetCriterion, hydrateCriterionWithBidAndId } from './utils';

const adGroupCriterionTypeName = 'Model.AdGroupCriterion';

export const readAdGroupCriterions = (cid, aid, campaignId, adGroupId, type) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdGroups.$withKey(adGroupId)
    .AdGroupCriterions
    .$cast(adGroupCriterionTypeName)
    .get({
      filter: {
        where: type,
      },
    })
    .toPromise();

export const readAdGroupLocationCriterions = (cid, aid, campaignId, adGroupId) =>
  readAdGroupCriterions(
    cid,
    aid,
    campaignId,
    adGroupId,
    "isof(Criterion,'Model.LocationCriterion') or isof(Criterion,'Model.RadiusCriterion') or isof(Criterion,'Model.LocationIntentCriterion')"
  );

export const modelToViewModelForAdgroupLocationTargets = ({
  model, campaignLocationModel, bundleCountries, adGroupId, campaignId,
}) => {
  const {
    locations,
    intentId,
    intentOption,
  } = parseLocationTargets(model, 'adGroup', adGroupId);

  // bundleCountries is based on campaign language settings because
  // 1. bundle is not used in ad group level location setting
  // 2. bundle is only used to compute TargetOption in viewmodel, currently only used for logging
  // if any further use, please take care of the bundleCountries, determine to use bundleCountries based on campaign language settings or ad group language settings
  const targetOption = locations.length ? targetOptions.specific : targetOptions.everywhere;
  // targetOption = shouldSelectBundleOption(bundleCountries, locations) ? targetOptions.bundle : targetOption;

  let parentLevelSettings;

  if (campaignLocationModel) {
    const {
      locations: campaignLocations,
      intentId: campaignIntentId,
      intentOption: campaignIntentOption,
    } = parseLocationTargets(campaignLocationModel, 'campaign', campaignId);

    let campaignTargetOption = campaignLocations.length ? targetOptions.specific : targetOptions.everywhere;
    campaignTargetOption = shouldSelectBundleOption(bundleCountries, campaignLocations) ? targetOptions.bundle : campaignTargetOption;

    parentLevelSettings = {
      initLocations: campaignLocations,
      currentLocations: campaignLocations,
      targetOption: campaignTargetOption,
      intentId: campaignIntentId,
      ...campaignIntentOption,
    };
  }

  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      parentLevelSettings,
      bidMode: false,
      useParentLevel: locations.length === 0,
    },
  };
};

export const readAdGroupScheduleCriterions = (cid, aid, campaignId, adGroupId) =>
  readAdGroupCriterions(
    cid, aid, campaignId, adGroupId,
    "isof(Criterion,'Model.DayTimeCriterion')"
  );

export const viewModelToBulkModelForAdGroupSchedule = (adGroupViewModel, campaign) => {
  if (_.isEmpty(adGroupViewModel.dayTimeRanges) && adGroupViewModel.adSchedulesBulkUpsert.every(schedule => schedule.Status === 'Deleted')) {
    return [];
  }
  const targets = adGroupViewModel.adSchedulesBulkUpsert || [];
  return targets.map((criterion) => {
    const Status = _.isEmpty(criterion) ? 'Active' : criterion.Status;
    if (adGroupViewModel.Id) {
      return {
        ...criterion,
        Status,
        AdGroup: {
          Id: adGroupViewModel.Id,
        },
      };
    }
    return {
      ...criterion,
      Status,
      AdGroupName: adGroupViewModel.Name,
      CampaignName: campaign.Name,
    };
  });
};

export const readAdGroupTargetCriterion = (cid, aid, campaignId, adGroupId) =>
  readAdGroupCriterions(
    cid, aid, campaignId, adGroupId,
    "isof(Criterion,'Model.AgeCriterion') or isof(Criterion,'Model.GenderCriterion') or isof(Criterion,'Model.DeviceCriterion') or isof(Criterion,'Model.CompanyNameCriterion') or isof(Criterion,'Model.IndustryCriterion') or isof(Criterion,'Model.JobFunctionCriterion')"
  );

export const AdGroupAgeName = ['EighteenToTwentyFive', 'TwentyFiveToThirtyFive', 'ThirtyFiveToFifty', 'FiftyToSixtyFive', 'SixtyFiveAndAbove', 'Unknown'];

export const modelToViewModelForAdGroupAgeTargets = ({ model, campaignId, adGroupId }) => {
  let initialAge = [];
  const ageTargets = AdGroupAgeName.map((name) => {
    const targetItem = model && model.find(target => target.Criterion.AgeRange === name);
    if (targetItem) {
      const isExcluded = targetItem['@odata.type'] === '#Model.NegativeAdGroupCriterion';
      return {
        Id: targetItem.Id,
        Status: targetItem.Status || 'Active',
        Criterion: _.extend(targetItem.Criterion, {
          AgeCriterion: { Target: targetItem.Criterion.AgeRange },
          ...isExcluded ? {} : { BidAdjustment: targetItem.CriterionBid?.Multiplier },
        }),
        CriterionType: isExcluded ? 'NegativeAge' : 'Age',
        Campaign: {
          Id: campaignId,
        },
        AdGroup: {
          Id: adGroupId,
        },
      };
    }
    return {
      Status: 'Active',
      Criterion: {
        '@odata.type': '#Model.AgeCriterion',
        Type: 'Age',
        AgeCriterion: { Target: name },
        BidAdjustment: 0,
      },
      CriterionType: 'Age',
      Campaign: {
        Id: campaignId,
      },
      AdGroup: {
        Id: adGroupId,
      },
    };
  });

  const ageRanges = ageTargets.filter(target => target.CriterionType === 'Age')
    .map(target => target.Criterion.AgeCriterion.Target);

  initialAge = cloneDeep(ageTargets);
  return {
    Age: ageTargets,
    initialAge,
    ageRanges,
  };
};

export const viewModelToBulkModelForAdGroupDeviceTargets = ({ adGroupViewModel }) => {
  const deviceTargets = adGroupViewModel.Device || [];
  const initialDevice = adGroupViewModel.initialDevice || [];
  const addedDevices = [];
  if (adGroupViewModel.currentSelectedDeviceLevel === 'campaign') {
    if (_.isEmpty(initialDevice)) {
      return [];
    }
    initialDevice.forEach((target) => {
      if (target.Id) {
        addedDevices.push({
          Criterion: _.extend({ BidAdjustment: 0 }, target.Criterion),
          Id: target.Id,
          Status: 'Deleted',
          CriterionType: target.Criterion.Type,
          AdGroup: {
            Id: adGroupViewModel.Id,
          },
          AdGroupId: adGroupViewModel.Id,
        });
      }
    });
    return addedDevices;
  }
  deviceTargets.forEach((target) => {
    addedDevices.push({
      Criterion: _.extend(target.Status === 'Deleted' ? { BidAdjustment: 0 } : {}, target.Criterion),
      Id: target.Id > 0 ? target.Id : null,
      Status: target.Status || 'Active',
      CriterionType: target.Criterion.Type,
      AdGroup: {
        Id: adGroupViewModel.Id,
      },
      AdGroupId: adGroupViewModel.Id,
    });
  });
  return addedDevices;
};

export const bulkModelToViewModelForAdGroupDeviceTargets = (targetsModel, update) => targetsModel.map((target) => {
  const newTarget = update.filter(t => t.Criterion.DeviceCriterion.DeviceName === target.Criterion.DeviceCriterion.DeviceName);
  if (newTarget.length > 0) {
    // eslint-disable-next-line no-param-reassign
    target.Criterion.Id = newTarget[0].Criterion.Id;
  }
  return target;
});

export const viewModelToBulkModelForAdGroupGenderTargets = ({ adGroupViewModel }) => {
  const genderTargets = adGroupViewModel.Gender || [];
  const initialGender = adGroupViewModel.initialGender || [];
  const addedGenders = [];
  if (adGroupViewModel.currentSelectedGenderLevel === 'campaign') {
    if (_.isEmpty(initialGender)) {
      return [];
    }
    initialGender.forEach((target) => {
      if (target.Id) {
        addedGenders.push({
          Criterion: _.extend({ BidAdjustment: 0 }, target.Criterion),
          Id: target.Id,
          Status: 'Deleted',
          CriterionType: target.Criterion.Type,
          AdGroup: {
            Id: adGroupViewModel.Id,
          },
          AdGroupId: adGroupViewModel.Id,
        });
      }
    });
    return addedGenders;
  }
  genderTargets.forEach((target) => {
    addedGenders.push({
      Criterion: _.extend(target.Status === 'Deleted' ? { BidAdjustment: 0 } : {}, target.Criterion),
      Id: target.Id > 0 ? target.Id : null,
      Status: target.Status || 'Active',
      CriterionType: target.Criterion.Type,
      AdGroup: {
        Id: adGroupViewModel.Id,
      },
      AdGroupId: adGroupViewModel.Id,
    });
  });
  return addedGenders;
};

export const bulkModelToViewModelForAdGroupGenderTargets = (targetsModel, update) => targetsModel.map((target) => {
  const newTarget = update.filter(t => t.Criterion.GenderCriterion.Target === target.Criterion.GenderCriterion.Target);
  if (newTarget.length > 0) {
    // eslint-disable-next-line no-param-reassign
    target.Criterion.Id = newTarget[0].Criterion.Id;
  }
  return target;
});

export const viewModelToBulkModelForAdGroupAgeTargets = ({ adGroupViewModel }) => {
  const ageTargets = adGroupViewModel.Age || [];
  const initialAge = adGroupViewModel.initialAge || [];
  const addedAges = [];
  if (adGroupViewModel.currentSelectedAgeLevel === 'campaign') {
    if (_.isEmpty(initialAge)) {
      return [];
    }
    initialAge.forEach((target) => {
      if (target.Id) {
        addedAges.push({
          Criterion: _.extend(target.Criterion, { BidAdjustment: 0 }),
          Id: target.Id,
          Status: 'Deleted',
          CriterionType: target.CriterionType,
          AdGroup: {
            Id: adGroupViewModel.Id,
          },
          AdGroupId: adGroupViewModel.Id,
        });
      }
    });
    return addedAges;
  }
  ageTargets.forEach((target) => {
    const initialTarget = initialAge.find(age => age.CriterionType === target.CriterionType && age.Criterion.AgeCriterion.Target === target.Criterion.AgeCriterion.Target);
    if (!initialTarget || !(target.Id > 0) || target.Criterion.BidAdjustment !== initialTarget.Criterion.BidAdjustment) {
      addedAges.push({
        Criterion: target.Criterion,
        Id: initialTarget && target.Id > 0 ? target.Id : null,
        Status: target.Status || 'Active',
        CriterionType: target.CriterionType,
        AdGroup: {
          Id: adGroupViewModel.Id,
        },
        AdGroupId: adGroupViewModel.Id,
      });
    }
  });
  const removedAges = initialAge.filter(initialTarget => initialTarget.Id > 0 && ageTargets.find(age => age.CriterionType === initialTarget.CriterionType && age.Criterion.AgeCriterion.Target === initialTarget.Criterion.AgeCriterion.Target) === undefined)
    .map(target => ({
      Criterion: _.extend(target.Criterion, { BidAdjustment: 0 }),
      Id: target.Id,
      Status: 'Deleted',
      CriterionType: target.CriterionType,
      AdGroup: {
        Id: adGroupViewModel.Id,
      },
      AdGroupId: adGroupViewModel.Id,
    }));
  return [...addedAges, ...removedAges];
};

export const bulkModelToViewModelForAdGroupAgeTargets = (targetsModel, update) => targetsModel.map((target) => {
  const newTarget = update.filter(t => t.Criterion.AgeCriterion.Target === target.Criterion.AgeCriterion.Target);
  if (newTarget.length > 0) {
    // eslint-disable-next-line no-param-reassign
    target.Criterion.Id = newTarget[0].Criterion.Id;
  }
  return target;
});

export const updateAdGroupAgeTargets = (targetsModel, ageRanges) => targetsModel.map((target) => {
  const isExcluded = ageRanges.find(ageRange => ageRange === target.Criterion.AgeCriterion.Target) === undefined;
  if (isExcluded) {
    if (target.CriterionType === 'Age') {
      return {
        ...target,
        Criterion: {
          ...target.Criterion,
          BidAdjustment: undefined,
        },
        CriterionType: 'NegativeAge',
      };
    }
  } else if (target.CriterionType === 'NegativeAge') {
    return {
      ...target,
      Criterion: {
        ...target.Criterion,
        BidAdjustment: 0,
      },
      CriterionType: 'Age',
    };
  }
  return target;
});

export const modelToViewModelForAdGroupCompanyNameTargets = ({ model, campaignViewModel, currentLevel }) => {
  let companyNameTargets = [];
  let initialCompanyNameTargets = [];
  if (model && !_.isEmpty(model)) {
    companyNameTargets = model.map(target => ({
      Id: target.Id,
      Target: target.Criterion.ProfileId,
      Size: target.Criterion.Size,
      Industry: target.Criterion.Industry,
      Name: target.Criterion.CompanyName,
      BidAdjustment: target.CriterionBid.Multiplier,
    }));
  }
  initialCompanyNameTargets = cloneDeep(companyNameTargets);
  return {
    companyNameTargets: currentLevel === 'campaign' ? campaignViewModel.companyNameTargets : companyNameTargets,
    initialCompanyNameTargets,
  };
};

export const viewModelToBulkModelForAdGroupCompanyNameTargets = ({ adGroupViewModel, campaignViewModel }) => {
  const companyNameTargets = adGroupViewModel.companyNameTargets || [];
  const initialCompanyNameTargets = adGroupViewModel.initialCompanyNameTargets || [];

  let addedCompanyNameTargets = [];
  let removedCompanyNameTargets = [];

  if (adGroupViewModel.currentSelectedCompanyNameLevel === 'campaign') {
    if (_.isEmpty(initialCompanyNameTargets)) {
      return [];
    }
    removedCompanyNameTargets = initialCompanyNameTargets.map(target => ({ ...target, criterion: target.Target, status: 'Deleted' }));
  } else {
    addedCompanyNameTargets = companyNameTargets.filter(target => initialCompanyNameTargets.find(initialTarget =>
      initialTarget.Target === target.Target && initialTarget.BidAdjustment === target.BidAdjustment) === undefined);
    removedCompanyNameTargets = initialCompanyNameTargets.filter(initialTarget => companyNameTargets.find(target =>
      initialTarget.Target === target.Target) === undefined).map(target => ({ ...target, criterion: target.Target, status: 'Deleted' }));
  }

  const addedCompanyNameCriterion = getTargetCriterion({
    targets: addedCompanyNameTargets.map(({ Id, ...criterion }) => ({ Id, criterion })), type: 'CompanyName', propKey: 'Target', level: 'adgroup', adGroupViewModel, campaignViewModel,
  });
  const removedCompanyNameCriterion = getTargetCriterion({
    targets: removedCompanyNameTargets, type: 'CompanyName', propKey: 'Target', level: 'adgroup', adGroupViewModel, campaignViewModel,
  });

  if (addedCompanyNameCriterion && addedCompanyNameCriterion.length > 0) {
    hydrateCriterionWithBidAndId('CompanyName', initialCompanyNameTargets, addedCompanyNameCriterion);
  }

  return [
    ...addedCompanyNameCriterion,
    ...removedCompanyNameCriterion,
  ];
};

export const modelToViewModelForAdGroupIndustryTargets = ({ model, campaignViewModel, currentLevel }) => {
  let industryTargets = [];
  let initialIndustry = [];
  if (model && !_.isEmpty(model)) {
    industryTargets = model.map(target => ({
      Id: target.Id,
      BidAdjustment: target.CriterionBid.Multiplier,
      criterion: target.Criterion.ProfileId,
    }));
  }
  initialIndustry = cloneDeep(industryTargets);
  return {
    industry: currentLevel === 'campaign' ? campaignViewModel.industry : industryTargets.map(target => target.criterion),
    initialIndustryTargets: currentLevel === 'campaign' ? campaignViewModel.initialIndustryTargets : initialIndustry,
    initialIndustry,
  };
};

export const viewModelToBulkModelForAdGroupIndustryTargets = ({ adGroupViewModel, campaignViewModel }) => {
  const industryTargets = adGroupViewModel.industry || [];
  const initialIndustryTargets = adGroupViewModel.initialIndustry || [];
  const { industryTargetsBidAdjustment } = adGroupViewModel;

  let addedIndustryTargets = [];
  let removedIndustryTargets = [];

  if (adGroupViewModel.currentSelectedIndustryLevel === 'campaign') {
    if (_.isEmpty(initialIndustryTargets)) {
      return [];
    }
    removedIndustryTargets = initialIndustryTargets.map(target => ({ ...target, status: 'Deleted' }));
  } else {
    const isIndustryTargetBidAdjustmentUpdated = ({ criterion, BidAdjustment }) => industryTargetsBidAdjustment.find(targetWithBid =>
      _.get(targetWithBid, 'Criterion.IndustryCriterion.Target') === criterion && _.get(targetWithBid, 'Criterion.BidAdjustment') === BidAdjustment);

    addedIndustryTargets = industryTargets.filter(target => initialIndustryTargets.find(initialTarget =>
      initialTarget.criterion === target && (!industryTargetsBidAdjustment || isIndustryTargetBidAdjustmentUpdated(initialTarget))) === undefined);
    removedIndustryTargets = initialIndustryTargets.filter(initialTarget => industryTargets.find(target =>
      initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
  }

  const addedIndustryCriterion = getTargetCriterion({
    targets: addedIndustryTargets.map(criterion => ({ criterion })), type: 'Industry', propKey: 'Target', level: 'adgroup', adGroupViewModel, campaignViewModel,
  });
  const removedIndustryCriterion = getTargetCriterion({
    targets: removedIndustryTargets, type: 'Industry', propKey: 'Target', level: 'adgroup', adGroupViewModel, campaignViewModel,
  });

  if (industryTargetsBidAdjustment && industryTargetsBidAdjustment.length > 0) {
    hydrateCriterionWithBidAndId('Industry', initialIndustryTargets, addedIndustryCriterion, industryTargetsBidAdjustment);
  }

  return [
    ...addedIndustryCriterion,
    ...removedIndustryCriterion,
  ];
};

export const modelToViewModelForAdGroupJobFunctionTargets = ({ model, campaignViewModel, currentLevel }) => {
  let jobFunctionTargets = [];
  let initialJobTargets = [];
  if (model && !_.isEmpty(model)) {
    jobFunctionTargets = model.map(target => ({
      Id: target.Id,
      BidAdjustment: target.CriterionBid.Multiplier,
      criterion: target.Criterion.ProfileId,
    }));
  }
  initialJobTargets = cloneDeep(jobFunctionTargets);
  return {
    jobTargets: currentLevel === 'campaign' ? campaignViewModel.jobTargets : jobFunctionTargets.map(target => target.criterion),
    initialJobFunctionTargets: currentLevel === 'campaign' ? campaignViewModel.initialJobFunctionTargets : initialJobTargets,
    initialJobTargets,
  };
};

export const viewModelToBulkModelForAdGroupJobFunctionTargets = ({ adGroupViewModel, campaignViewModel }) => {
  const jobTargets = adGroupViewModel.jobTargets || [];
  const initialJobTargets = adGroupViewModel.initialJobTargets || [];
  const { jobTargetBidAdjustments } = adGroupViewModel;

  let addedJobTargets = [];
  let removedJobTargets = [];

  if (adGroupViewModel.currentSelectedJobFunctionLevel === 'campaign') {
    if (_.isEmpty(initialJobTargets)) {
      return [];
    }
    removedJobTargets = initialJobTargets.map(target => ({ ...target, status: 'Deleted' }));
  } else {
    const isJobTargetBidAdjustmentUpdated = ({ criterion, BidAdjustment }) => jobTargetBidAdjustments.find(targetWithBid =>
      _.get(targetWithBid, 'Criterion.JobFunctionCriterion.Target') === criterion && _.get(targetWithBid, 'Criterion.BidAdjustment') === BidAdjustment);

    addedJobTargets = jobTargets.filter(target => initialJobTargets.find(initialTarget =>
      initialTarget.criterion === target && (!jobTargetBidAdjustments || isJobTargetBidAdjustmentUpdated(initialTarget))) === undefined);
    removedJobTargets = initialJobTargets.filter(initialTarget => jobTargets.find(target =>
      initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
  }

  const addedJobCriterion = getTargetCriterion({
    targets: addedJobTargets.map(criterion => ({ criterion })), type: 'JobFunction', propKey: 'Target', level: 'adgroup', adGroupViewModel, campaignViewModel,
  });
  const removedJobCriterion = getTargetCriterion({
    targets: removedJobTargets, type: 'JobFunction', propKey: 'Target', level: 'adgroup', adGroupViewModel, campaignViewModel,
  });

  if (jobTargetBidAdjustments && jobTargetBidAdjustments.length > 0) {
    hydrateCriterionWithBidAndId('JobFunction', initialJobTargets, addedJobCriterion, jobTargetBidAdjustments);
  }

  return [
    ...addedJobCriterion,
    ...removedJobCriterion,
  ];
};

export const modelToViewModelForAdGroupLocationTargets = ({
  model, campaignLocationModel, bundleCountries, adGroupId, campaignId,
}) => {
  const {
    locations,
    intentId,
    intentOption,
  } = parseLocationTargets(model, 'adGroup', adGroupId);

  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;

  let parentLevelSettings;

  if (campaignLocationModel) {
    const {
      locations: campaignLocations,
      intentId: campaignIntentId,
      intentOption: campaignIntentOption,
    } = parseLocationTargets(campaignLocationModel, 'campaign', campaignId);

    let campaignTargetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
    campaignTargetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;

    parentLevelSettings = {
      initLocations: campaignLocations,
      currentLocations: campaignLocations,
      targetOption: campaignTargetOption,
      intentId: campaignIntentId,
      ...campaignIntentOption,
    };
  }

  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      parentLevelSettings,
      bidMode: true,
      useParentLevel: locations.length === 0,
    },
  };
};

export const viewModelToBulkModelForAdGroupLocationTargets = (bundleLocationCriterions, adGroupViewModel, campaignViewModel) => {
  if (!adGroupViewModel.locationTargets) {
    return [];
  }
  const bulkUpsertModel = convertLocationStateToLocationSchemaCampaign(
    adGroupViewModel.locationTargets,
    bundleLocationCriterions,
    {
      bidStrategy: {
        option: '',
      },
      ...campaignViewModel,
    },
    {
      ...adGroupViewModel,
      name: {
        value: adGroupViewModel.Name,
      },
    }
  );
  return bulkUpsertModel;
};

export const bulkModelToViewModelForAdGroupLocationTargets = (locationTargetsModel, bundleCountries, originaladGroupModel) => {
  const locations = locationTargetsModel
    .filter(criterion => criterion.Criterion.Type === 'Location' || criterion.Criterion.Type === 'Radius');
  const intent = locationTargetsModel.find(criterion => criterion.Criterion.Type === 'LocationIntentCriterion');
  const intentId = intent ? intent.Id : null;
  const intentOption = intent ? getLocationIntent(intent.Criterion.LocationIntentCriterion.IntentOption) : null;
  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;
  const parentLevelSettings = originaladGroupModel.locationTargets && originaladGroupModel.locationTargets.parentLevelSettings;
  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      parentLevelSettings,
      bidMode: true,
      useParentLevel: locations.length === 0,
    },
  };
};
